import React, { useEffect, useState } from 'react';

import {
  Combobox,
  ComboboxLabel,
  ComboboxOption,
} from '@/components/ui/catalyst/combobox';
import { useGetFirmsQuery } from '@/services/api/firmsService';

interface FirmOption {
  id: string;
  name: string;
}

interface FirmComboboxProps {
  value: string;
  onChange: (firmId: string) => void;
  className?: string;
  panelClassName?: string;
}

export const FirmCombobox: React.FC<FirmComboboxProps> = ({
  value,
  onChange,
  className = 'min-w-[140px]',
  panelClassName = '',
}) => {
  const { data: firms = [], isLoading } = useGetFirmsQuery({});
  const [selectedFirm, setSelectedFirm] = useState<FirmOption | undefined>(
    undefined,
  );

  // Convert firms to options and handle current selection
  const firmOptions = firms.map((firm) => ({
    id: firm.id,
    name: firm.name,
  }));

  // Update selected firm when value changes
  useEffect(() => {
    if (value) {
      const found = firmOptions.find((firm) => firm.id === value);
      setSelectedFirm(found || undefined);
    } else {
      setSelectedFirm(undefined);
    }
  }, [value, firmOptions]);

  const handleSelectionChange = (firm: FirmOption | null) => {
    const firmValue = firm || undefined;
    setSelectedFirm(firmValue);
    onChange(firmValue?.id || '');
  };

  const displayValue = (firm: FirmOption | null) => {
    return firm?.name || '';
  };

  const filterFirms = (firm: FirmOption, query: string) => {
    return firm.name.toLowerCase().includes(query.toLowerCase());
  };

  if (isLoading) {
    return (
      <div
        className={`${className} px-2 py-1 text-xs text-gray-500 border border-gray-300 rounded`}
      >
        Loading firms...
      </div>
    );
  }

  return (
    <Combobox
      value={selectedFirm}
      onChange={handleSelectionChange}
      options={firmOptions}
      displayValue={displayValue}
      filter={filterFirms}
      placeholder="Select firm..."
      className={className}
      panelClassName={panelClassName}
    >
      {(firm) => (
        <ComboboxOption key={firm.id} value={firm}>
          <ComboboxLabel>{firm.name}</ComboboxLabel>
        </ComboboxOption>
      )}
    </Combobox>
  );
};
